import arrayMove from 'array-move';
import * as React from 'react';
import {
  DndContext,
  closestCenter,
  DragOverlay,
  defaultDropAnimationSideEffects,
  DropAnimation,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { DragHandleProvider } from '../../hooks/useDragHandle';
import { BUTTON, INPUT, SELECT, TEXTAREA } from '../../utils/tagNames';

/**
 * Drop animation configuration.
 */
const dropAnimationConfig: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.4',
      },
    },
  }),
};

/**
 * Drag overlay to mirror the active (dragged) item.
 */
const SortableOverlay = ({ children }) => (
  <DragOverlay dropAnimation={dropAnimationConfig}>{children}</DragOverlay>
);

/**
 * Custom keyboard sensor that respects certain element types and uses Enter or Space to activate.
 */
export class KeyboardSensorThatHandlesElementType extends KeyboardSensor {
  static activators = [
    {
      eventName: 'onKeyDown',
      handler: ({ nativeEvent: event }: React.KeyboardEvent<Element>) => {
        if (!shouldHandleEvent(event)) {
          return false;
        }
        return event.key === 'Enter' || event.key === ' ';
      },
    },
  ];
}

/**
 * Custom pointer sensor that respects certain element types.
 */
export class PointerSensorThatHandlesElementType extends PointerSensor {
  static activators = [
    {
      eventName: 'onPointerDown',
      handler: ({ nativeEvent: event }: React.PointerEvent<Element>) =>
        shouldHandleEvent(event),
    },
  ];
}

/**
 * Helper function to determine whether the event should be handled.
 */
function shouldHandleEvent(event: React.SyntheticEvent) {
  if ((event as React.KeyboardEvent).key === 'Tab') {
    return false;
  }
  const activeElement = document.activeElement;
  if (
    activeElement &&
    ([INPUT, SELECT, TEXTAREA, BUTTON].includes(activeElement.tagName) ||
      activeElement.getAttribute('contenteditable') === 'true')
  ) {
    return false;
  }
  return true;
}

/**
 * A helper component that wraps (clones) an existing element with sortable functionality.
 */
const SortableItem = ({
  helperClass = '',
  id,
  itemElement,
  order,
  useDragHandle = false,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ data: { accessibleLabel: `Row ${order}` }, id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const tabIndex =
    itemElement.props.tabIndex !== undefined ? itemElement.props.tabIndex : 0;

  const mergedAttributes = {
    ...attributes,
    'aria-label': `Row ${order}`,
    role: attributes.role,
  };

  const clonedElement = React.cloneElement(itemElement, {
    'data-id': id,
    className: `${itemElement.props.className || ''} ${helperClass}`.trim(),
    ref: setNodeRef,
    style: { ...(itemElement.props.style || {}), ...style },
    tabIndex,
    ...listeners,
    ...mergedAttributes,
  });

  if (useDragHandle) {
    return (
      <DragHandleProvider
        attributes={{ ...mergedAttributes, tabIndex: -1 }}
        listeners={listeners}
      >
        {clonedElement}
      </DragHandleProvider>
    );
  }

  return clonedElement;
};

/**
 * The main SortableList component.
 */
export const SortableList = ({
  data,
  helperClass,
  sortCallback,
  useDragHandle,
}) => {
  const [activeId, setActiveId] = React.useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensorThatHandlesElementType, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensorThatHandlesElementType, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      const oldIndex = data.findIndex(
        (item) => String(item.key) === String(active.id)
      );
      const newIndex = data.findIndex(
        (item) => String(item.key) === String(over.id)
      );
      const newData = arrayMove(data, oldIndex, newIndex);
      sortCallback(newData);
      setTimeout(() => {
        const movedElement = document.querySelector(
          `[data-id=\"${active.id}\"]`
        );
        movedElement?.focus();
      });
    }
    setActiveId(null);
  };

  const handleDragStart = ({ active }) => {
    setActiveId(active.id);
  };

  const renderItemById = (id, applyHelperClass = false) => {
    const index = data.findIndex((item) => String(item.key) === String(id));
    const itemElement = data[index];
    const computedOrder = index >= 0 ? index + 1 : 'unknown';
    return (
      <SortableItem
        helperClass={applyHelperClass ? helperClass : ''}
        id={itemElement.key}
        itemElement={itemElement}
        key={String(itemElement.key)}
        order={computedOrder}
        useDragHandle={useDragHandle}
      />
    );
  };

  return (
    <DndContext
      accessibility={{
        announcements: {
          onDragCancel: ({ active }) =>
            `Drag cancelled for ${active.data.current?.accessibleLabel}`,
          onDragEnd: ({ active, over }) =>
            over
              ? `Dropped ${active.data.current?.accessibleLabel} over ${over.data.current?.accessibleLabel || over.id}`
              : `Dropped ${active.data.current?.accessibleLabel}`,
          onDragOver: ({ active }) =>
            `${active.data.current?.accessibleLabel} currently being dragged`,
          onDragStart: ({ active }) =>
            `Picked up ${active.data.current?.accessibleLabel}`,
        },
      }}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      sensors={sensors}
    >
      <SortableContext
        items={data.map((item) => item.key)}
        strategy={verticalListSortingStrategy}
      >
        {data.map((item, i) => (
          <SortableItem
            helperClass={helperClass}
            id={item.key}
            itemElement={item}
            key={String(item.key)}
            order={i + 1}
            useDragHandle={useDragHandle}
          />
        ))}
      </SortableContext>
      <SortableOverlay>
        {activeId ? renderItemById(activeId, true) : null}
      </SortableOverlay>
    </DndContext>
  );
};
